import {hasSubCategory, type IdValue} from '../../../../utils/companyCategorySubCategory';

export const environmentalMetrics = [
  {
    key: '1',
    metric: 'Scope 1 GHG Emissions',
    average: {text: '500 tCO₂e', color: 'cyan'},
    gap: '40% higher emissions',
    insight: 'Improve energy efficiency and adopt EVs'
  },
  {
    key: '2',
    metric: 'Scope 2 GHG Emissions',
    average: {text: '1200 tCO₂e', color: 'cyan'},
    gap: '25% higher emissions',
    insight: 'Improve energy efficiency and adopt EVs'
  },
  {
    key: '3',
    metric: 'Scope 3 GHG Emissions',
    average: {text: '15000 tCO₂e', color: 'cyan'},
    gap: '20% higher emissions',
    insight: 'Improve energy efficiency and adopt EVs'
  },
  {
    key: '4',
    metric: 'Energy Consumption (Renewable/Non-renewable)',
    average: {text: '60% non-renewable', color: 'cyan'},
    gap: 'High dependency on non-renewables',
    insight: 'Improve energy efficiency and adopt EVs'
  },
  {
    key: '5',
    metric: 'Water Consumption',
    average: {text: '250,000 KL', color: 'cyan'},
    gap: '20% higher consumption',
    insight: 'Improve energy efficiency and adopt EVs'
  },
  {
    key: '6',
    metric: 'Water Recycling Rate',
    average: {text: '45%', color: 'cyan'},
    gap: '33% lower recycling',
    insight: 'Enhance water recycling systems'
  },
  {
    key: '7',
    metric: 'Waste Management (Haz/Non-Haz)',
    average: {text: '70% segregated', color: 'cyan'},
    gap: '30% lower waste segregation',
    insight: 'Segregate and manage waste more effectively'
  },
  {
    key: '8',
    metric: 'Biodiversity Impact Management',
    average: {text: 'Plan in place', color: 'cyan'},
    gap: 'No biodiversity safeguards',
    insight: 'Develop and implement a biodiversity strategy'
  }
];

export const socialMetrics = [
  {
    key: '1',
    metric: 'Workforce Diversity',
    average: {text: '40% gender diversity', color: 'green'},
    gap: '15% less gender diversity',
    insight: 'Enhance diversity hiring initiatives'
  },
  {
    key: '2',
    metric: 'Board Diversity',
    average: {text: '30% women on board', color: 'green'},
    gap: '20% fewer women on board',
    insight: 'Promote women into board roles'
  },
  {
    key: '3',
    metric: 'Training Hours / Employee',
    average: {text: '25 hours/year', color: 'green'},
    gap: '40% less training',
    insight: 'Increase investment in employee training'
  },
  {
    key: '4',
    metric: 'Employee Turnover Rate',
    average: {text: '12%', color: 'green'},
    gap: '8% higher turnover',
    insight: 'Improve work culture and retention strategies'
  },
  {
    key: '5',
    metric: 'OHS – Injury Rates',
    average: {text: '2.5 injuries / 100 FTE', color: 'green'},
    gap: '60% higher injury rate',
    insight: 'Improve safety training and monitoring'
  },
  {
    key: '6',
    metric: 'Human Rights Policy',
    average: {text: 'Policy published', color: 'green'},
    gap: 'Policy not yet finalized',
    insight: 'Finalize and implement human rights policy'
  },
  {
    key: '7',
    metric: 'Supply-Chain Social Assessments',
    average: {text: '80% suppliers assessed', color: 'green'},
    gap: '50% fewer suppliers assessed',
    insight: 'Expand supplier social assessments'
  }
];

export const governanceMetrics = [
  {
    key: '1',
    metric: 'ESG Oversight by Board',
    average: {text: 'Yes', color: 'yellow'},
    gap: 'No ESG oversight',
    insight: 'Assign ESG responsibilities to board committee'
  },
  {
    key: '2',
    metric: 'Anti-Bribery / Anti-Corruption Policy',
    average: {text: 'Yes', color: 'yellow'},
    gap: 'Policy lacks enforcement',
    insight: 'Strengthen anti-bribery controls'
  }
];

// sub_category_changes
export const subCategoryWiseMetrics = (
  subCategory: IdValue
): {
  environmentalMetrics: any[];
  socialMetrics: any[];
  governanceMetrics: any[];
} => {
  if (hasSubCategory(subCategory, 83)) {
      return {
        environmentalMetrics: [
          {
            key: '1',
            metric: 'Scope 1 GHG Emissions',
            average: {text: '350,000 tCO₂e', color: 'cyan'},
            gap: 'Likely higher intensity due to lack of advanced emission controls',
            insight: 'Adopt cleaner fuels, reduce refrigerant emissions'
          },
          {
            key: '2',
            metric: 'Scope 2 GHG Emissions',
            average: {text: '230,000 tCO₂e', color: 'cyan'},
            gap: 'Likely high dependency on grid electricity',
            insight: 'Shift to renewable PPAs & rooftop solar'
          },
          {
            key: '3',
            metric: 'Scope 3 GHG Emissions',
            average: {text: '2.9 million tCO₂e', color: 'cyan'},
            gap: 'Likely not measured or disclosed',
            insight: 'Build supply-chain carbon tracking system'
          },
          {
            key: '4',
            metric: 'Energy Consumption (Renewable)',
            average: {text: '53% renewable', color: 'cyan'},
            gap: 'Likely lower renewable share',
            insight: 'Increase solar, biomass, and hybrid energy systems'
          },
          {
            key: '5',
            metric: 'Water Consumption',
            average: {text: '1.2 million KL', color: 'cyan'},
            gap: 'Potentially inefficient usage (no benchmarks visible)',
            insight: 'Implement water intensity KPIs & reuse systems'
          },
          {
            key: '6',
            metric: 'Water Recycling Rate',
            average: {
              text: 'ZLD-driven (no % but high reuse in peers)',
              color: 'cyan'
            },
            gap: 'Likely limited or unquantified recycling',
            insight: 'Install ZLD and track recycling %'
          },
          {
            key: '7',
            metric: 'Waste Management (Haz/Non-Haz)',
            average: {text: 'Moderate segregation maturity', color: 'cyan'},
            gap: 'Likely weak classification & reporting',
            insight: 'Implement waste segregation + circular practices'
          },
          {
            key: '8',
            metric: 'Biodiversity Impact Management',
            average: {text: 'Basic compliance level', color: 'cyan'},
            gap: 'No structured biodiversity assessment',
            insight: 'Conduct EIA and develop biodiversity plan'
          }
        ],
        socialMetrics: [
          {
            key: '1',
            metric: 'Workforce Diversity',
            average: {text: '15% female workforce', color: 'green'},
            gap: 'Likely lower or untracked diversity',
            insight: 'Improve hiring diversity & inclusion policies'
          },
          {
            key: '2',
            metric: 'Board Diversity',
            average: {text: '19–20% women', color: 'green'},
            gap: 'Likely below benchmark',
            insight: 'Increase independent women directors'
          },
          {
            key: '3',
            metric: 'Training Hours / Employee',
            average: {text: '20–22 hrs/year', color: 'green'},
            gap: 'Likely unstructured training metrics',
            insight: 'Introduce measurable L&D programs'
          },
          {
            key: '4',
            metric: 'Employee Turnover Rate',
            average: {text: '8–10%', color: 'green'},
            gap: 'Unknown / untracked',
            insight: 'Strengthen retention strategy'
          },
          {
            key: '5',
            metric: 'OHS – Injury Rates',
            average: {text: '0.03 LTIR (best-in-class)', color: 'green'},
            gap: 'Likely higher or unreported',
            insight: 'Implement safety KPIs & monitoring systems'
          },
          {
            key: '6',
            metric: 'Human Rights Policy',
            average: {text: '100% peers have policy', color: 'green'},
            gap: 'Likely missing or basic',
            insight: 'Formalize HR policy + audits'
          },
          {
            key: '7',
            metric: 'Supply-Chain Social Assessments',
            average: {text: 'Moderate maturity', color: 'green'},
            gap: 'Likely minimal supplier ESG checks',
            insight: 'Introduce supplier audits & ESG screening'
          }
        ],

        governanceMetrics: [
          {
            key: '1',
            metric: 'ESG Oversight by Board',
            average: {text: '100% (strong governance)', color: 'yellow'},
            gap: 'Likely limited ESG governance structure',
            insight: 'Create ESG committee / board oversight'
          },
          {
            key: '2',
            metric: 'Anti-Bribery / Anti-Corruption Policy',
            average: {text: '100% compliance', color: 'yellow'},
            gap: 'Likely basic or informal controls',
            insight: 'Formalize policy + whistleblower system'
          }
        ]
      };
  }

  return {environmentalMetrics, socialMetrics, governanceMetrics};
};
